import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { GridCloseIcon, GridColDef } from '@mui/x-data-grid';
import { Badge, Box, Button, FormHelperText, IconButton, Typography } from '@mui/material';
import { TInboundFields } from '../../Models/Inbound';
import Table from '../custom/Table';
import Title from '../custom/Title';
import DynamicForm, { TDynamicFieldProps, TField, getFormConfigDeepCopy, useDynamicForm } from '../forms/DynamicFormNew';
import useService from '../../hooks/useService';
import { getGoldCategoriesOptions, updateTransaction, settleInbound as settleInboundService, getTransitionHistory, getLotDetailsCount, getPvcCenters, createTransactionImage, getTransactionImages, deleteTransactionImage, fetchBankDetails, fetchHSNCodeDetails, deleteTransactionInvoice, createTransactionInvoice, getTransactionInvoice } from '../../services/Transactions';
import { TGetGoldCategoriesData } from '../../services/Transactions/types';
import { TOption } from '../../Models/common';
import { BANK_OPTIONS, BUY_SELL_STEPS, FLOAT_REGEX, IFSC_REGEX, INBOUND_PRODUCT_TYPE, TRANSACTION_STATUS, TRANSACTION_TYPES } from '../../utils/constant';
import { isEmpty ,formatDate} from '../../utils/helpers';
import Camera from '../Modals/Camera';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import Tooltip from '@mui/material/Tooltip';
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

const Divider = styled.div`
    border: 1px solid #EBEBEC;
`

export type TCategoryOption = {
    [key: string]: {
        label: string,
        value: string,
        parant?: string
    }
}

export type TCategoryWithChildrenOtion = {
    [key: string]: {
        label: string,
        value: string,
        children: TCategoryOption;
    }
}


export const INBOUND_MAIN_CONFIG: TField<TInboundFields>[] = [
    {
        fieldName: 'shipmentWeight',
        isVisible: true,
        fieldType: 'text',
        label: 'Shipment Weight',
        width: 'calc(33% - 20px)',
        decimalPlaces:3,
        regexFilter:FLOAT_REGEX,
        validation: {
            required: true
        }
    },
    {
        fieldName: 'hologramStickerNumber',
        isVisible: false,
        fieldType: 'text',
        label: 'Hologram Sticker No',
        width: 'calc(33% - 20px)',
    },
    {
        fieldName: 'productType',
        isVisible: false,
        fieldType: 'select',
        label: 'Product Type',
        width: 'calc(33% - 20px)',
        options: Object.entries(INBOUND_PRODUCT_TYPE).map(option => ({ label: option[0], value: option[1] })),
    },
    {
        fieldName: 'recycledMetalCategory',
        isVisible: false,
        label: 'Recycled Gold Category',
        fieldType: 'select',
        options: [],
        width: 'calc(33% - 20px)',
    },
    {
        fieldName: 'recycledMetalSubcategory',
        isVisible: false,
        label: 'Recycled Gold Subcategory',
        fieldType: 'select',
        options: [],
        width: 'calc(33% - 20px)'
    },
    {
        fieldName: 'hsnCode',
        isVisible: true,
        label: 'HSN Code For Inbound Line',
        fieldType: 'autocomplete',
        options: [],
        width: 'calc(33% - 20px)',
        validation: {
            required: true
        }
    },
    {
        fieldName: 'secondaryTransactionLocation',
        isVisible: false,
        label: 'Secondary Location',
        fieldType: 'autocomplete',
        options: [],
        width: 'calc(33% - 20px)',
    },
    {
        fieldName: 'conversionFineness',
        isVisible: false,
        label: 'Conversion Fineness',
        fieldType: 'select',
        width: 'calc(33% - 20px)',
        disabled:true,
        options: [
            { label: "995", value: 995 },
            // { label: "999", value: 999 },
            // { label: "999.9", value: 999.9 },
        // { label: "1000", value: 1000 },
        // { label: "999.99", value: 999.99 },
        // { label: "999.95", value: 999.95 },
        // { label: "999.5", value: 999.5 },
        // { label: "990", value: 990 },
        // { label: "930", value: 930 },
        // { label: "916.7", value: 916.7 },
        // { label: "800", value: 800 },
        // { label: "500", value: 500 }
        ],
    },
    {
        fieldName: 'customerReferenceNumber',
        isVisible: false,
        label: 'Customer Reference Number',
        fieldType: 'text',
        width: 'calc(33% - 20px)',
    },
    {
        fieldName: 'customerTaxInvoiceDeclaredValue',
        isVisible: false,
        fieldType: 'text',
        label: 'Cust. Tax Invoice Declared Value',
        width: 'calc(33% - 20px)',
        decimalPlaces:2,
        regexFilter:FLOAT_REGEX
    },
    {
        fieldName: 'customerTaxInvoicePreTaxValue',
        isVisible: false,
        fieldType: 'text',
        label: 'Cust. Tax Invoice Pre Tax Value',
        width: 'calc(33% - 20px)',
        decimalPlaces:2,
        regexFilter:FLOAT_REGEX
    },
    {
        fieldName: 'customerTaxInvoiceRatePerGm',
        isVisible: false,
        fieldType: 'text',
        label: 'Cust. Tax Invoice Rate Per Gram',
        width: 'calc(33% - 20px)',
        decimalPlaces:2,
        regexFilter:FLOAT_REGEX
    },
    {
        fieldName: 'customerTaxInvoiceTaxPercentage',
        isVisible: false,
        fieldType: 'number',
        label: 'Cust. Tax Invoice Tax Percentage',
        width: 'calc(33% - 20px)',
        disabled: true,
        initialValue: 3
    },
    {
        fieldName: 'customerDeliveryChallanNumber',
        isVisible: false,
        fieldType: 'text',
        label: 'Cust. Delivery Challan Number',
        width: 'calc(33% - 20px)'
    },
    {
        fieldName: 'customerTaxInvoiceDate',
        isVisible: false,
        fieldType: 'date',
        label: 'Cust. Tax Invoice Date',
        width: 'calc(33% - 20px)',
        disableFuture: true
    },
    {
        fieldName: 'bankName',
        isVisible: false,
        label: 'Bank Name',
        width: 'calc(33% - 20px)',
        fieldType: 'autocomplete',
        options: [],
    },
    {
        fieldName: 'accountName',
        label: 'Account Name',
        width: 'calc(33% - 20px)',
        isVisible: false,
        fieldType: 'text',
    },
    {
        fieldName: 'typeOfDeposit',
        isVisible: false,
        fieldType: 'text',
        label: '',
        width: 'calc(33% - 20px)',
        // options: [{ label: 'STBD', value: "STBD" }, { label: 'MTGD', value: "MTGD" }, { label: 'LTGD', value: "LTGD" },],
    },
    {
        fieldName: 'customerAccNo',
        isVisible: false,
        fieldType: 'number',
        label: 'Customer Account No',
        width: 'calc(33% - 20px)',
        validation: {
            maxLength: 20,
            minLength: 10,
        }
    },
    {
        fieldName: 'ifscCode',
        isVisible: false,
        fieldType: 'text',
        label: 'IFSC Code',
        width: 'calc(33% - 20px)',
    },
    {
        fieldName: 'bankDocumentDate',
        isVisible: false,
        fieldType: 'date',
        label: 'Bank Document Date',
        width: 'calc(33% - 20px)'
    },
    {
        fieldName: 'bankAddress',
        isVisible: false,
        fieldType: 'text',
        label: 'Bank Address',
        width: 'calc(33% - 20px)'
    },
    {
        fieldName: 'remarks',
        isVisible: true,
        fieldType: 'text',
        label: 'Remarks',
        width: 'calc(33% - 20px)'
    },
    {
        fieldName: 'buybackTDS',
        fieldType: 'number',
        isVisible: false,
        label: 'Buyback TDS',
        width: 'calc(33% - 20px)',
        disabled: true,
    },
    {
        fieldName: 'buybackType',
        fieldType: 'select',
        label: 'Buyback Type',
        isVisible: false,
        width: 'calc(33% - 20px)',
        options: [
            { label: 'MMTC-PAMP Product', value: 'MMTC_PAMP_PRODUCT' },
            { label: 'Melting', value: 'MELTING' },
            // { label: 'Non Melting', value: 'NON_MELTING' },
        ],
        // disabled:true,
    },
    {
        fieldName: 'transactionImage',
        isVisible: true,
        fieldType: 'custom',
        label: 'Image',
        width: 'calc(33% - 20px)',
        validation: {
            required: false
        }
    },
    {
        fieldName: 'transactionInvoice',
        isVisible: true,
        fieldType: 'custom',
        label: 'Document upload',
        width: 'calc(33% - 20px)',
        validation: {
            required: false
        }
    }
]

const TRANSITION_HISTORY_COLUMNS = [
    {
        id: "id",
        label: "Id"
    },
    {
        id: "fromStatus",
        label: "From Status"
    },
    {
        id: "toStatus",
        label: "To Status"
    },
    {
        id: "time",
        label: "Time"
    },
    {
        id: "user",
        label: "User Name"
    },
    {
        id: "type",
        label: "Transaction Type"
    },
    {
        id: "recordId",
        label: "Record Id"
    },
    {
        id: "createdAt",
        label: "Created At"
    }
]

const mapCategories = (allCategories: TGetGoldCategoriesData) => {
    const categories: TCategoryWithChildrenOtion = {} as TCategoryWithChildrenOtion
    const subCategories: TCategoryOption = {}

    for (let category of allCategories) {
        if (!category.parent) categories[category.value] = { label: category.label, value: category.value, children: {} }
        else subCategories[category.value] = { label: category.label, value: category.value, parant: category.parent }
    }
    console.log('categories', categories)
    console.log('subCategories', subCategories)
    for (let categoryKey in subCategories) {
        const category = subCategories[categoryKey]
        categories[category.parant!] = { ...categories[category.parant!], children: { ...categories[category.parant!].children, [categoryKey]: { label: category.label, value: category.value } } }
    }

    return categories
}

const UploadImage = (props: TDynamicFieldProps<TInboundFields>) => {
    const { execute, store } = useService();
    const { transactionsStore, notificationStore } = store;
    const { transactionInfo, transactionImages } = transactionsStore;
    const { notify } = notificationStore;
    const { error } = props;
    const [ openCameraModal, setOpenCameraModal ] = useState(false);
    const [ hoveredImage, setHoveredImage ] = useState('');

    const captureImage = async (imageDataUrl: string) => {
        const { data, error } = await execute(() => createTransactionImage({ globalTransactionNumber: transactionInfo.globalTransactionNumber!, transactionImage: imageDataUrl, transactionType: transactionInfo.transactionType }));
        if (error === null && data !== null) {
            notify('Image Uploaded.');
        }
    }

    const deleteImage = async (id: string) => {
        const { data, error } = await execute(() => deleteTransactionImage({ id, globalTransactionNumber: transactionInfo.globalTransactionNumber!, transactionType: transactionInfo.transactionType }));
        if (error === null && data !== null) {
            notify('Image Deleted.');
        }
    }

    const downloadImage = (imageUrl: string, idx: number) => {
        const anchor = document.createElement('a');
        anchor.style.display = 'none';
        anchor.href = imageUrl;
        anchor.download =  `${transactionInfo.globalTransactionNumber!}-${idx+1}`;
        anchor.click();
        anchor.remove();
    }

    return (
      <>
        <div>
          <div>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setOpenCameraModal(true)}
            >
              {transactionImages.length > 0 ? "Add More" : "Upload Image"}
            </Button>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              rowGap: "15px",
              marginTop: "15px",
            }}
          >
            {transactionImages.length > 0 ? (
              transactionImages.map((elem, idx) => {
                return (
                  <div
                    key={elem.id}
                    style={{ marginRight: "15px" }}
                    onMouseEnter={() => setHoveredImage(elem.id)}
                    onMouseLeave={() => setHoveredImage("")}
                  >
                    <Badge
                      badgeContent={
                        <CloseIcon
                          style={{ fontSize: "11px", cursor: "pointer" }}
                          onClick={() => deleteImage(elem.id)}
                        />
                      }
                      color="error"
                    >
                      <img src={elem.transactionImage} width={100} />
                      <Tooltip title="Download" placement="top-end">
                        <DownloadIcon
                          style={{
                            position: "absolute",
                            bottom: "2px",
                            right: "2px",
                            cursor: "pointer",
                            opacity: hoveredImage === elem.id ? 1 : 0,
                            transition: "opacity 0.3s ease",
                          }}
                          htmlColor="black"
                          onClick={() =>
                            downloadImage(elem.transactionImage, idx)
                          }
                        />
                      </Tooltip>
                    </Badge>
                  </div>
                );
              })
            ) : (
              <></>
            )}
          </div>
        </div>
        {error ? <FormHelperText error>{error}</FormHelperText> : <></>}
        <Camera
          title="Take a picture"
          open={openCameraModal}
          clearOnSave={true}
          closeHandler={() => setOpenCameraModal(false)}
          saveImage={captureImage}
        ></Camera>
      </>
    );
}

const UploadTxnInvoice = (props: TDynamicFieldProps<TInboundFields>) => {
  const { execute, store } = useService();
  const { transactionsStore, notificationStore } = store;
  const { transactionInfo, transactionInvoices } = transactionsStore;
  const { notify } = notificationStore;
  const { error } = props;

  const [hoveredFile, setHoveredFile] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ---------- file → base64 ----------
  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });

  // ---------- upload ----------
  const uploadFile = async (file: File) => {
    const base64 = await fileToBase64(file);

    const { data, error } = await execute(() =>
      createTransactionInvoice({
        globalTransactionNumber: transactionInfo.globalTransactionNumber!, transactionInvoice: base64, transactionType: transactionInfo.transactionType
      })
    );

    if (!error && data) notify('File Uploaded.');
  };

  // ---------- delete ----------
  const deleteFile = async (id: string) => {
    const { data, error } = await execute(() =>
      deleteTransactionInvoice({
        id, globalTransactionNumber: transactionInfo.globalTransactionNumber!, transactionType: transactionInfo.transactionType
      })
    );

    if (!error && data) notify('File Deleted.');
  };

  // ---------- download ----------
  const downloadFile = (fileUrl: string, idx: number) => {
    const anchor = document.createElement('a');
    anchor.href = fileUrl;
    anchor.download = `${transactionInfo.globalTransactionNumber!}-${idx + 1}`;
    anchor.click();
    anchor.remove();
  };

  // ---------- input change ----------
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;

    for (const file of Array.from(files)) {
      await uploadFile(file);
    }

    e.target.value = '';
  };

  const isPdf = (url: string) => /\.pdf($|\?)/i.test(url);

  return (
    <>
      <div>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => fileInputRef.current?.click()}
        >
          {transactionInvoices.length > 0
            ? 'Add More'
            : 'Document upload'}
        </Button>

        <input
          ref={fileInputRef}
          type="file"
          hidden
          multiple
          accept="image/*,.pdf"
          onChange={handleFileChange}
        />

        {/* ---------- grid ---------- */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
            gap: '10px', 
            marginTop: '15px',
            justifyContent: 'flex-start',
          }}
        >
          {transactionInvoices.map((elem, idx) => (
            <div
                key={elem.id}
                style={{
                    marginRight: "15px",
                    position: "relative",
                    width: 100,
                }}
                >
                {/* Delete Button */}
                <IconButton
                    size="small"
                    onClick={() => deleteFile(elem.id)}
                    sx={{
                    position: "absolute",
                    top: -8,
                    right: -8,
                    bgcolor: "white",
                    boxShadow: 1,
                    zIndex: 10,
                    "&:hover": { bgcolor: "#f5f5f5" },
                    }}
                >
                    <GridCloseIcon fontSize="small" />
                </IconButton>

                {/* Preview Box */}
                <Box
                    sx={{
                    width: 100,
                    height: 100,
                    border: "1px solid #ddd",
                    borderRadius: 1,
                    overflow: "hidden",
                    bgcolor: "#fafafa",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    cursor: "pointer",
                    "&:hover .download-btn": {
                        opacity: 1,
                    },
                    }}
                >
                    {/* Preview */}
                    {isPdf(elem.transactionInvoice) ? (
                        <Box
                            sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "#fff4f4",
                            border: "1px solid #f5c2c2",
                            borderRadius: 1,
                            }}
                        >
                            <PictureAsPdfIcon
                                sx={{ fontSize: 42, mb: 0.5 }}
                                color="error"
                            />
                            <Typography
                                variant="caption"
                                fontWeight={600}
                                color="error"
                            >
                                PDF
                            </Typography>
                        </Box>
                        ) : (
                        <img
                            src={elem.transactionInvoice}
                            alt="preview"
                            style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            }}
                        />
                    )}
                    {/* Download */}
                    <Tooltip title="Download">
                    <IconButton
                        className="download-btn"
                        size="small"
                        onClick={() =>
                        downloadFile(elem.transactionInvoice, idx)
                        }
                        sx={{
                        position: "absolute",
                        bottom: 4,
                        right: 4,
                        bgcolor: "white",
                        opacity: 0,
                        transition: "0.2s",
                        boxShadow: 1,
                        "&:hover": { bgcolor: "#f5f5f5" },
                        }}
                    >
                        <DownloadIcon fontSize="small" />
                    </IconButton>
                    </Tooltip>
                </Box>
            </div>
          ))}
        </div>
      </div>

      {error && <FormHelperText error>{error}</FormHelperText>}
    </>
  );
};

function MainDetails(props: { goToNextTab: () => any, goToNextStep: () => any }) {
    const { goToNextTab, goToNextStep } = props
    const { execute, store } = useService()
    const { transactionsStore, customerStore, notificationStore, authStore } = store
    const { pvcCenters, inboundDetails, transitionHistory, meltingLot, setCapturedSteps, transactionInfo, charges, transactionImages, physicalOrder, transactionInvoices } = transactionsStore
    const { status: transactionStatus, globalTransactionNumber, transactionType, lotCounts, } = transactionInfo
    const { customerConfig } = customerStore
    const { notify, confirm } = notificationStore
    const { user } = authStore
    const [categoriesOptions, setCategoriesOptions] = useState<TCategoryWithChildrenOtion>({} as TCategoryWithChildrenOtion)
    const [currentCategory, setCurrentCategory] = useState('')
    const [secondaryLocationOptions, setSecondaryLocationOptions] = useState<TOption[]>([])
    const [timer, setTimer] = useState<NodeJS.Timeout>()
    const [forceNavigate, setForceNavigate] = useState(false)

    // const searchLocation = (searchString: string) => {
    //     if (timer) clearTimeout(timer)
    //     if (searchString.length < 2) return
    //     const timeout = setTimeout(() => {
    //         execute(() => searchPvcCenters({ searchString }))
    //             .then(({ data, error }) => {
    //                 if (data !== null) setSecondaryLocationOptions(data.map(location => ({ label: location.name, value: location.id! })))
    //             })
    //     }, 500)
    //     setTimer(timeout)
    // }

    const { registerForm, setFormConfig, formData, setFormData, setField } = useDynamicForm({
        formConfig: getFormConfigDeepCopy(INBOUND_MAIN_CONFIG)
    })

    console.log('formData...', formData)
    useEffect(() => {

        setFormData(prev => ({ ...prev, customerReferenceNumber: customerConfig.customerRefNo! }))

        setFormConfig(prev => {

            /*
            const allFields: (keyof TInboundFields)[] = [
                'bankAddress', 'bankDocumentDate', 'bankName', 'buybackTDS', 'buybackType',
                'conversionFineness', 'customerAccNo', 'customerDeliveryChallanNumber', 'customerTaxInvoiceDate',
                'customerTaxInvoiceDeclaredValue', 'customerTaxInvoicePreTaxValue', 'customerTaxInvoiceRatePerGm',
                'customerTaxInvoiceTaxPercentage', 'hologramStickerNumber', 'hsnCode', 'ifscCode', 'priority', 'productType',
                'recycledMetalCategory', 'recycledMetalSubcategory', 'remarks', 'secondaryTransactionLocationId',
                'shipmentWeight'
            ]

            const alwaysShownFields : (keyof TInboundFields)[] = [
                'shipmentWeight', 'hsnCode', 'remarks'
            ]
            */

            const gmsFields: (keyof TInboundFields)[] = [
                'bankAddress', 'bankDocumentDate', 'bankName',
                'customerAccNo', 'hologramStickerNumber', 'ifscCode',
                'customerReferenceNumber', 'typeOfDeposit','recycledMetalCategory','recycledMetalSubcategory'
            ]

            const purityTestingFields: (keyof TInboundFields)[] = [
                'hologramStickerNumber', 'productType',
                'recycledMetalCategory', 'recycledMetalSubcategory'
            ]

            const B2BCustomerBuybackFields: (keyof TInboundFields)[] = [
                'customerTaxInvoiceDate',
                'customerTaxInvoiceDeclaredValue',
                'customerTaxInvoicePreTaxValue',
                'customerTaxInvoiceRatePerGm',
                'customerTaxInvoiceTaxPercentage',
                'customerReferenceNumber'
            ];

            const buybackFields: (keyof TInboundFields)[] = [
                'buybackTDS',
                'hologramStickerNumber',
                'productType',
                'recycledMetalCategory',
                'recycledMetalSubcategory',
                'secondaryTransactionLocation',
                'customerAccNo',
                'bankName',
                'ifscCode',
                'bankAddress',
                'accountName',
                ...(customerConfig?.isCorporate === 'Y'
                    ? B2BCustomerBuybackFields
                    : [])
            ];

            const buysellFields: (keyof TInboundFields)[] = [
                'conversionFineness', 'customerTaxInvoiceDate',
                'customerTaxInvoiceDeclaredValue', 'customerTaxInvoicePreTaxValue', 'customerTaxInvoiceRatePerGm',
                'customerTaxInvoiceTaxPercentage', 'hologramStickerNumber', 'productType',
                'recycledMetalCategory', 'recycledMetalSubcategory', 'customerReferenceNumber', 'secondaryTransactionLocation'
            ]

            const jobworkFields: (keyof TInboundFields)[] = [
                'conversionFineness', 'customerTaxInvoiceDate',
                'customerTaxInvoiceDeclaredValue', 'customerTaxInvoicePreTaxValue', 'customerTaxInvoiceRatePerGm',
                'customerTaxInvoiceTaxPercentage', 'hologramStickerNumber', 'productType',
                'recycledMetalCategory', 'recycledMetalSubcategory', 'customerDeliveryChallanNumber', 'secondaryTransactionLocation'
            ]

            return [...prev.map(field => {

                switch (transactionType) {
                    case TRANSACTION_TYPES.GMS:
                        if (gmsFields.includes(field.fieldName)) {
                            field.isVisible = true;
                            field.validation = field.validation ? { ...field.validation, required: true } : { required: true };
                            if(field.fieldName === 'ifscCode'){
                                field.validation = field.validation ? { ...field.validation, required: true , regex : IFSC_REGEX } : { required: true };
                            } else if (field.fieldName === 'bankDocumentDate') {
                                field.disableFuture = true;
                            } else if(field.fieldName === 'typeOfDeposit') {
                                field.disabled = true;
                            }
                        }
                        break;

                    case TRANSACTION_TYPES.PURITY_TESTING:
                        if (purityTestingFields.includes(field.fieldName)) {
                            field.isVisible = true;
                            field.validation = field.validation ? { ...field.validation, required: true } : { required: true };
                        }
                        break;

                    case TRANSACTION_TYPES.BUY_BACK:
                        if (buybackFields.includes(field.fieldName)) {
                            field.isVisible = true;
                            field.validation = field.validation ? { ...field.validation, required: true } : { required: true };
                        }
                        break;

                    case TRANSACTION_TYPES.BUY_SELL:
                        if (buysellFields.includes(field.fieldName)) {
                            field.isVisible = true;
                            field.validation = field.validation ? { ...field.validation, required: true } : { required: true };
                        }
                        break;

                    case TRANSACTION_TYPES.JOB_WORK:
                        if (jobworkFields.includes(field.fieldName)) {
                            field.isVisible = true;
                            field.validation = field.validation ? { ...field.validation, required: true } : { required: true };
                        }
                        break;

                    default:
                        break;
                }

                if(field.fieldName === 'transactionImage') {
                    field.RenderField = UploadImage;
                }

                if(field.fieldName === 'transactionInvoice') {
                    field.RenderField = UploadTxnInvoice;
                }

                if (field.fieldName === 'hologramStickerNumber') {
                    field.validation = { ...field.validation, required: false };
                }
                
                if (field.fieldName === 'secondaryTransactionLocation' && field.isVisible) {
                    field.validation = field.validation ? { ...field.validation, required: false } : { required: false };
                }

                return field
            })]
        })
    }, [])

    const submitHandler = (data: TInboundFields) => {
        if (!transactionImages || transactionImages.length === 0) {
            notify('Please upload at least one transaction image before updating.', 'error');
            return;
        }

        if (!transactionInvoices || transactionInvoices.length === 0) {
            notify('Please upload at least one transaction invoice before updating.', 'error');
            return;
        }
        
        delete data.billingAddressId;
        delete data.shippingAddressId;
        if(!isEmpty(data.customerTaxInvoicePreTaxValue)  && !isEmpty(data.customerTaxInvoiceDeclaredValue)){
            if(Number(data.customerTaxInvoicePreTaxValue) > Number(data.customerTaxInvoiceDeclaredValue)) {
                return notify('CUSTOMER TAX INVOICE PRE TAX VALUE cannot be greater than CUSTOMER TAX INVOICE DECLARED VALUE', 'error')
            }
        }
        updateMainDetails(data)
    }

    const settleInbound = async () => {
        const totalMeltingLotWeight = meltingLot.reduce((acc, item) => {
            return item.netWeight + acc
        }, 0);
        // todo:
        // if (totalMeltingLotWeight !== inboundDetails.shipmentWeight) {
            // notify('Net weight of lots should be equal to Shipment weight', 'error')
        // } else {
            const { error } = await execute(() => settleInboundService({
                globalTransactionNumber: globalTransactionNumber!,
                transactionType,
                charges
            }))
            if (error === null) {
                notify('Inbound Settled Successfully')
                setCapturedSteps(prev => ([...prev, 'INBOUND']))
                setForceNavigate(true)
            }
        // }
    }

    const captureHandler = () => {
        if (!transactionImages || transactionImages.length === 0) {
            notify('Please upload at least one transaction image before updating.', 'error');
            return;
        }

        if (!transactionInvoices || transactionInvoices.length === 0) {
            notify('Please upload at least one transaction invoice before updating.', 'error');
            return;
        }
        
        confirm({
            handleConfirm: () => settleInbound(),
            text: 'Click \'Yes\' to Complete the Settlement.',
            title: 'Settlement Confirmation'
        })
    }

    const getCategories = async () => {
        const { data, error } = await execute(() => getGoldCategoriesOptions({ type: 'RECYCLED_GOLD_CATAGORY RECYCLED_GOLD_SUBCATAGORY' }))

        if (error === null && data !== null) {
            const categories = mapCategories(data)
            console.log('categories :::', categories)
            setCategoriesOptions(categories)
        }

    }

    const mapCategoriesToOptions = () => {
        const options: { label: string, value: any }[] = []
        for (let key in categoriesOptions) {
            const category = categoriesOptions[key]
            options.push({ value: category.value, label: category.label })
        }
        setFormConfig(prev => {
            return [...prev.map(config => {
                if (config.fieldName === 'recycledMetalCategory') {
                    return { ...config, options: options }
                } else return config
            })]
        })
    }

    const mapSubcategoriesToOptions = (categoryValue: string) => {
        console.log('category value...', categoryValue)
        const options: { label: string, value: any }[] = []
        const categories = categoriesOptions[categoryValue].children
        for (let key in categories) {
            const category = categories[key]
            options.push({ value: category.value, label: category.label })
        }
        setFormConfig(prev => {
            return [...prev.map(config => {
                if (config.fieldName === 'recycledMetalSubcategory') {
                    return { ...config, options: options }
                } else return config
            })]
        })
    }

    const updateMainDetails = async (inboundHeader: TInboundFields) => {
        const { data, error } = await execute(() => updateTransaction({ inbound: { inboundHeader, globalTransactionNumber: globalTransactionNumber! }, transactionType }));

        if (error === null && data !== null) {
            notify('Inbound Details Submitted.');

            if (
                (transactionType === TRANSACTION_TYPES.BUY_BACK && transactionStatus === TRANSACTION_STATUS.PAYMENT_INITIATED) ||
                (transactionType === TRANSACTION_TYPES.GMS && Object.keys(physicalOrder || {}).length > 0)
            ) {
                setCapturedSteps(prev => ([...prev, 'INBOUND']));
                goToNextStep();
            } else {
                goToNextTab();
            }
        }
    };

    const getLocations = () => {
        execute(() => getPvcCenters({}))
    }

    const setSecondaryLocationField = () => {
        setFormConfig(prev => {
            return [...prev.map(field => {
                if (field.fieldName === 'secondaryTransactionLocation') {
                    field = { ...field, options: pvcCenters.filter(center => center.id !== user.centerId).map(center => ({ label: center.name, value: center.id! })) }
                }
                return field
            })]
        })
        
    }

    const setSecondaryLocation = () => {
        console.log('inbound details...', transactionInfo)
        const secondaryTransactionLocationId = transactionInfo.secondaryTransactionLocationId
        console.log('secondaryTransactionLocation', secondaryTransactionLocationId, pvcCenters)
        if (secondaryTransactionLocationId && pvcCenters.length) {
            setFormData(prev => ({ ...prev, secondaryTransactionLocation: secondaryTransactionLocationId }))
        }
    }

    const fetchTransitionHistory = async () => {
        const payload = {
            type: BUY_SELL_STEPS.INBOUND.toUpperCase(),
            recordId: inboundDetails.id!
        }
        const { data } = await execute(() => getTransitionHistory({ transactionType, ...payload }))
    }

    const getTransactionColumns = (): GridColDef<any>[] => {
        let minWidth = 145
     
      
        return TRANSITION_HISTORY_COLUMNS.filter(transitionHistoryField => transitionHistoryField.label !== "Time").map(transitionHistoryField => {
            return {
                field: transitionHistoryField.id,
                headerName: transitionHistoryField.label,
                minWidth: transitionHistoryField.id === "id" ? 100 : minWidth,
                valueFormatter: (params) => {
                    if (params.field === 'createdAt' && params.value) {
                        return formatDate(new Date(params.value));
                    }
                    return params.value;
                }
            }
        })
    }

    const getLotDetailsCountFun = async () => {
        const { data } = await execute(() => getLotDetailsCount({ transactionType, globalTransactionNumber: globalTransactionNumber! }));
    }

    const fetchTransactionImages = async () => {
        const { data } = await execute(() => getTransactionImages({ globalTransactionNumber: globalTransactionNumber! }));
    }

    const fetchTransactionInvoices = async () => {
        const { data } = await execute(() => getTransactionInvoice({ globalTransactionNumber: globalTransactionNumber! }));
    }

    const getBankOptions = async () => {
        const { data, error } = await execute(() => fetchBankDetails({ transactionType: transactionInfo.transactionType }));
    
        if (!error && data) {
            setField('bankName', {
                options: data.map((item: any) => ({
                    value: item.bank_name,
                    label: item.bank_name
                }))
            })
        }
    }

    const getHSNCode = async () => {
        const { data, error } = await execute(() => fetchHSNCodeDetails({ transactionType: transactionInfo.transactionType }));
    
        if (!error && data) {
            setField('hsnCode', {
                options: data.map((item: any) => ({
                    value: item.hsn_code,
                    label: item.hsn_code
                }))
            })
        }
    }

    useEffect(() => {
        getCategories()
        setFormData({ 
            ...inboundDetails, 
            customerTaxInvoiceTaxPercentage: 3, 
            typeOfDeposit: 'STBD',
            buybackTDS: (transactionType === TRANSACTION_TYPES.BUY_BACK ? 0.1 : undefined) as number, 
            conversionFineness: [TRANSACTION_TYPES.BUY_SELL, TRANSACTION_TYPES.JOB_WORK].includes(transactionType) ? 995 : undefined as any
            // buybackType: (transactionType === TRANSACTION_TYPES.BUY_BACK ? 'MMTC-PAMP Product' : undefined) as string 
        })
        getLotDetailsCountFun();
        getLocations();
        fetchTransactionImages();
        getBankOptions();
        getHSNCode();
        fetchTransactionInvoices();
    }, [])

    useEffect(() => {
        if (inboundDetails.id) {
            fetchTransitionHistory()
        }
    }, [inboundDetails])

    useEffect(() => {
        setFormConfig(prev => {
            return prev.map(field => {
                return field.fieldName === 'secondaryTransactionLocation'
                    ? { ...field, options: secondaryLocationOptions }
                    : field
            })
        })
    }, [secondaryLocationOptions])

    useEffect(() => {
        mapCategoriesToOptions()
    }, [categoriesOptions])

    useEffect(() => {
        if (formData.recycledMetalCategory && formData.recycledMetalCategory !== currentCategory && Object.keys(categoriesOptions).length) {
            mapSubcategoriesToOptions(formData.recycledMetalCategory)
            setCurrentCategory(formData.recycledMetalCategory)
            setFormData(prev => ({ ...prev, 'recycledGoldSubcategory': '' }))
        }
    }, [formData, categoriesOptions])

    useEffect(() => {
        setSecondaryLocationField()
    }, [pvcCenters])

    useEffect(() => {
        setSecondaryLocation()
    }, [transactionInfo])

    useEffect(() => {
        if (forceNavigate) goToNextStep()
    }, [forceNavigate])
    
    useEffect(() => {
        setFormConfig(prev => (
            prev.map(field => {
                if (lotCounts) {
                    if (field.fieldName === 'conversionFineness' || field.fieldName === 'buybackType') {
                        field.disabled = true;
                    }
                } else {
                    if (field.fieldName === 'shipmentWeight' || field.fieldName === 'conversionFineness' || field.fieldName === 'buybackType') {
                        field.disabled = false;
                    }
                }
                return field;
            })
        ))

    }, [lotCounts])

    console.log("register form ==>", formData)

    return (
        <div style={{ padding: '0 10px' }}>
            <div style={{ borderBottom: '1px solid #d8d8d8' }}>
                <Title style={{ textAlign: 'left', margin: '20px 10px 10px 10px', color: '#7f7d76' }}>Inbound Details</Title>
            </div>
            <div style={{ padding: '20px 0px' }}>
                <DynamicForm 
                    {...registerForm()} 
                    onSubmit={
                        transactionStatus === TRANSACTION_STATUS.INBOUND_INITIATED || 
                        transactionStatus === TRANSACTION_STATUS.PHYSICAL_ORDER_INITIATED ||  transactionStatus === TRANSACTION_STATUS.PAYMENT_INITIATED || 
                        (transactionType === TRANSACTION_TYPES.BUY_BACK && transactionStatus === TRANSACTION_STATUS.PAYMENT_INITIATED) ||
                        transactionType === TRANSACTION_TYPES.GMS
                        ? submitHandler 
                        : undefined
                    } 
                    submitText='Update' 
                />
            </div>
            <div >
                <Title style={{ textAlign: 'left', margin: '20px 10px 10px 10px', color: '#7f7d76' }}>Status Transition History</Title>
            </div>
            <div style={{ margin: '0px 3px' }}>
                <Table columns={getTransactionColumns()} rows={transitionHistory} />
            </div>
            <Divider />
            {transactionStatus === TRANSACTION_STATUS.INBOUND_INITIATED ? <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '10px' }}>
                    <Button variant='outlined' type='submit' color='secondary' onClick={captureHandler}>{'Settle'}</Button>
                </div>
            </div> : <></>}
        </div>
    )
}

export default MainDetails